import { createSelector } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import type { EspState } from '@/store/esp/state.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { AppState } from '@/store/store.ts';
import { createAppSelector } from '@/store/storeMethods.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

interface CurrencyPairStuff {
  currencyPair?: string;
  currencyPairPrecision: number;
}

export const selectCurrencyPairInfo = createAppSelector(
  [(state: AppState) => state, (_state, tileId: string) => tileId],
  (state, tileId): CurrencyPairStuff => {
    const currencyPair = state.option[tileId]?.properties.currencyPair ?? undefined;

    return {
      currencyPair,
      currencyPairPrecision: selectCurrencyPairPrecision(state, currencyPair),
    };
  },
);

function selectEsp(state: AppState): EspState {
  return state.esp;
}

function selectTiering(state: AppState, tileId: string): string | undefined {
  const tile = state.option[tileId];
  return tile?.properties.tiering;
}

export const selectEspSpot = createSelector(
  [selectCurrencyPairInfo, selectEsp, selectTiering],
  ({ currencyPair }, esp, tiering) => {
    if (isNotDefined(currencyPair) || isNotDefined(tiering)) {
      return undefined;
    }
    const espStreamId = getEspStreamId(currencyPair, tiering);
    const espPrice = esp.streams[espStreamId];

    if (isNotDefined(espPrice) || espPrice.status !== 'PRICED' || espPrice.priceType !== 'ESP.TRADER.PRICE') {
      return undefined;
    }

    const precision = espPrice.currencyPairPrecision;

    return convertPointsToRate(espPrice.midRate, precision);
  },
);
